import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { stepStatusMeta } from '../../utils/stepStatus';
import CurrentStepPanel from './CurrentStepPanel';
import CompletedStepEditPanel from './CompletedStepEditPanel';
import RequiredDocumentsPanel from './RequiredDocumentsPanel';
import Button from '../ui/Button';
import { revertCandidateStep } from '../../api/candidates';
import { pushToast } from '../ui/toastBus';

function Glyph({ children }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3.5 w-3.5"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const IconUserPlus = () => (
  <Glyph>
    <circle cx="10" cy="8.5" r="3" />
    <path d="M4.5 19c.6-3 2.6-4.6 5.5-4.6s4.9 1.6 5.5 4.6" />
    <path d="M18 8v4M20 10h-4" />
  </Glyph>
);
const IconFileText = () => (
  <Glyph>
    <path d="M6 3.5h8l4 4V20a.5.5 0 0 1-.5.5h-11A.5.5 0 0 1 6 20V4a.5.5 0 0 1 .5-.5z" />
    <path d="M9 13h6M9 16.5h6" />
  </Glyph>
);
const IconShieldCheck = () => (
  <Glyph>
    <path d="M12 3.5l6.5 2.8v4.6c0 4.3-2.7 7.9-6.5 9.3-3.8-1.4-6.5-5-6.5-9.3V6.3L12 3.5z" />
    <path d="M9 12l2 2 4-4" />
  </Glyph>
);
const IconMonitor = () => (
  <Glyph>
    <rect x="3.5" y="5" width="17" height="11" rx="1.5" />
    <path d="M9 20h6M12 16v4" />
  </Glyph>
);
const IconUsers = () => (
  <Glyph>
    <circle cx="8.5" cy="8.5" r="2.6" />
    <path d="M3.5 19c.6-2.9 2.4-4.5 5-4.5s4.4 1.6 5 4.5" />
    <circle cx="16.5" cy="8.5" r="2.1" />
    <path d="M15 14.7c1.9.4 3.1 1.8 3.5 4.3" />
  </Glyph>
);
const IconStar = () => (
  <Glyph>
    <path d="M12 3.5l2.6 5.4 5.9.7-4.3 4.1 1.1 5.9L12 16.7l-5.3 2.9 1.1-5.9-4.3-4.1 5.9-.7L12 3.5z" />
  </Glyph>
);
const IconHandshake = () => (
  <Glyph>
    <path d="M2.5 12.5l4-3.5 3 2.5 2.5-2 3 2.5 3.5-3" />
    <path d="M8.5 11.5l3.5 3.5a1.6 1.6 0 0 0 2.3-2.2" />
    <path d="M12.5 12.7l1.8 1.8a1.6 1.6 0 0 0 2.3-2.2" />
    <path d="M2.5 12.5l3 5 2 .5M21.5 11l-3 5.5-2 .8" />
  </Glyph>
);
const IconSend = () => (
  <Glyph>
    <path d="M4 20l16.5-8L4 4l1.8 7.2L14 12l-8.2.8L4 20z" />
  </Glyph>
);
const IconStamp = () => (
  <Glyph>
    <path d="M9 3.5h6a2 2 0 0 1 2 2V10a3 3 0 0 1-3 3H10a3 3 0 0 1-3-3V5.5a2 2 0 0 1 2-2z" />
    <path d="M8 17h8M4.5 20.5h15" />
    <path d="M7 13.5l-1.5 3.5M17 13.5l1.5 3.5" />
  </Glyph>
);
const IconPlaneLanding = () => (
  <Glyph>
    <path d="M3.5 20h17" />
    <path d="M4 14.5l6.5-1.8 5.7 2.4a1.6 1.6 0 0 0 2-2.2L13 8.5 10.5 4l-1.7.5 1 5-4-.8-1.6-2.2-1.5.4 1 3.7-2 .6z" />
  </Glyph>
);
const IconRefresh = () => (
  <Glyph>
    <path d="M4.5 12a7.5 7.5 0 0 1 12.6-5.5L19 8" />
    <path d="M19 4v4h-4" />
    <path d="M19.5 12a7.5 7.5 0 0 1-12.6 5.5L5 16" />
    <path d="M5 20v-4h4" />
  </Glyph>
);
const IconBook = () => (
  <Glyph>
    <path d="M4 5.5c1.8-1 4.2-1.2 6 0v13c-1.8-1.2-4.2-1-6 0z" />
    <path d="M20 5.5c-1.8-1-4.2-1.2-6 0v13c1.8-1.2 4.2-1 6 0z" />
  </Glyph>
);
const IconStethoscope = () => (
  <Glyph>
    <path d="M6 4v6a4 4 0 0 0 8 0V4" />
    <path d="M10 14v2a4.5 4.5 0 0 0 9 0v-1.5" />
    <circle cx="19.5" cy="10.5" r="1.6" />
  </Glyph>
);
const IconIdCard = () => (
  <Glyph>
    <rect x="3.5" y="5.5" width="17" height="13" rx="2" />
    <circle cx="8.5" cy="11" r="1.8" />
    <path d="M6 15.3c.4-1.3 1.3-2 2.5-2s2.1.7 2.5 2" />
    <path d="M13.5 9.5h4M13.5 13h4" />
  </Glyph>
);
const IconHeartPulse = () => (
  <Glyph>
    <path d="M12 20s-7.5-4.5-9.3-9.6C1.6 6.8 4 4 7 4c2 0 3.6 1.2 5 3 1.4-1.8 3-3 5-3 3 0 5.4 2.8 4.3 6.4C19.5 15.5 12 20 12 20z" />
    <path d="M6 12h3l1.5-2.5L12 14l1.5-2h3.5" />
  </Glyph>
);
const IconBank = () => (
  <Glyph>
    <path d="M3.5 9.5L12 4l8.5 5.5" />
    <path d="M4.5 9.5h15V11h-15zM5.5 11v7.5M9.5 11v7.5M14.5 11v7.5M18.5 11v7.5" />
    <path d="M3.5 20.5h17" />
  </Glyph>
);
const IconCar = () => (
  <Glyph>
    <path d="M4 16v-3.2L6 8.5A2 2 0 0 1 7.8 7.5h8.4A2 2 0 0 1 18 8.5l2 4.3V16" />
    <path d="M4 16h16M4 16v2.2M20 16v2.2" />
    <circle cx="7.2" cy="16" r="1.5" />
    <circle cx="16.8" cy="16" r="1.5" />
  </Glyph>
);
const IconFileCheck = () => (
  <Glyph>
    <path d="M6 3.5h8l4 4V20a.5.5 0 0 1-.5.5h-11A.5.5 0 0 1 6 20V4a.5.5 0 0 1 .5-.5z" />
    <path d="M9 14l2 2 4-4" />
  </Glyph>
);
const IconCreditCard = () => (
  <Glyph>
    <rect x="3.5" y="6" width="17" height="12" rx="2" />
    <path d="M3.5 10h17" />
    <path d="M6.5 14.5h4" />
  </Glyph>
);
const IconArchive = () => (
  <Glyph>
    <rect x="3.5" y="4.5" width="17" height="4" rx="1" />
    <path d="M5 8.5V19a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8.5" />
    <path d="M10 12.5h4" />
  </Glyph>
);
const IconDot = () => (
  <Glyph>
    <circle cx="12" cy="12" r="2" />
  </Glyph>
);

const STEP_ICONS = {
  registration: IconUserPlus,
  docs_for_cid: IconFileText,
  cid_approval: IconShieldCheck,
  online_screening: IconMonitor,
  f2f_final_interview: IconUsers,
  f2f_interview: IconUsers,
  shortlisted: IconStar,
  confirmation: IconHandshake,
  manager_approval: IconShieldCheck,
  visa_submission: IconSend,
  visa_approval: IconStamp,
  arrival: IconPlaneLanding,
  status_change: IconRefresh,
  english_test: IconBook,
  data_sheet: IconFileText,
  medical_eid_typing: IconStethoscope,
  visa_stamping: IconStamp,
  emirates_id: IconIdCard,
  medical_insurance: IconHeartPulse,
  bank_account: IconBank,
  driving_school: IconCar,
  pta_training: IconUsers,
  pcc_lcc: IconFileCheck,
  e_test: IconMonitor,
  rta_medical: IconStethoscope,
  permit_issuance: IconFileCheck,
  swipe_card: IconCreditCard,
  file_submission_dtc: IconArchive,
  hiring: IconHandshake,
};

function StepIcon({ code }) {
  const Icon = STEP_ICONS[code] || IconDot;
  return <Icon />;
}

export function formatAnswer(value) {
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '—';
  if (value === undefined || value === null || value === '') return '—';
  return String(value);
}

function StepHistory({ step }) {
  const checklistEntries = Object.entries(step.checklist_state || {});
  const metaEntries = Object.entries(step.meta || {}).filter(([k]) => k !== 'attempts');
  const fields = step.fields || [];

  if (!step.remarks && checklistEntries.length === 0 && metaEntries.length === 0 && fields.length === 0) {
    return <p className="text-sm text-gray-400">No additional details.</p>;
  }

  return (
    <div className="space-y-2 text-sm text-gray-600">
      {step.remarks && <p>{step.remarks}</p>}
      {checklistEntries.length > 0 && (
        <ul className="list-inside list-disc">
          {checklistEntries.map(([item, checked]) => (
            <li key={item} className={checked ? 'text-gray-700' : 'text-gray-400 line-through'}>
              {item}
            </li>
          ))}
        </ul>
      )}
      {metaEntries.map(([key, value]) => (
        <p key={key}>
          <span className="font-medium">{key}:</span> {String(value)}
        </p>
      ))}
      {fields.length > 0 && (
        <div>
          <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Fields</h4>
          <div className="space-y-1">
            {fields.map((f) => (
              <p key={f.id}>
                <span className="font-medium text-gray-700">{f.label}:</span> {formatAnswer(step.answers?.[f.id])}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Field labels + document type names a carried-over step is still missing for the new
// category's version of it — shared between the per-step banner below and the workspace-level
// "needs review after conversion" summary.
export function missingConversionDetails(step) {
  return [...(step.missing_fields || []).map((f) => f.label), ...(step.missing_documents || [])];
}

export function needsConversionReview(step) {
  return !!step.converted_from_category && missingConversionDetails(step).length > 0;
}

function isHistoryExpandable(step) {
  return !step.is_current && ['completed', 'approved', 'passed', 'rejected', 'failed', 'skipped'].includes(step.status);
}

// A pending step that sorts before the current one — typically left behind by a workflow
// reorder. It can be completed out of order to catch the candidate's record back up.
export function isGapStep(step, candidate) {
  if (step.status !== 'pending') return false;
  const current = candidate.steps.find((s) => s.is_current);
  return !!current && step.sort_order < current.sort_order;
}

export function useSelectedStep(candidate) {
  const currentStep = candidate.steps.find((s) => s.is_current);
  const [selectedId, setSelectedId] = useState(
    currentStep?.candidate_step_id ?? candidate.steps[0]?.candidate_step_id ?? null
  );
  const lastCurrentId = useRef(currentStep?.candidate_step_id ?? null);

  useEffect(() => {
    const newCurrentId = currentStep?.candidate_step_id ?? null;
    if (newCurrentId && newCurrentId !== lastCurrentId.current) {
      setSelectedId(newCurrentId);
    }
    lastCurrentId.current = newCurrentId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep?.candidate_step_id]);

  const selectedStep = candidate.steps.find((s) => s.candidate_step_id === selectedId) || currentStep;
  return [selectedStep, setSelectedId];
}

function StepIconItem({ step, idx, sectionLength, candidate, selectedStep, onSelect }) {
  const meta = stepStatusMeta(step.status);
  const gap = isGapStep(step, candidate);
  // Left behind by an early "Next" (WorkflowEngine::processNext): still in_progress with
  // section(s) open, but the candidate has already moved on to a later step. The pulsing ring
  // is reserved for the one truly current step, so this gets a plain amber ring instead.
  const leftBehind = !step.is_current && step.status === 'in_progress';
  const overdue = step.is_current && step.is_overdue;
  const needsReview = needsConversionReview(step);
  const selectable = step.is_current || isHistoryExpandable(step) || step.status === 'pending' || leftBehind;
  const isSelected = selectedStep?.candidate_step_id === step.candidate_step_id;
  const title = overdue
    ? `${step.title} — overdue by ${step.days_overdue}d (SLA: ${step.sla_days}d)`
    : needsReview
      ? `${step.title} — carried over, missing details for the new workflow`
      : gap
        ? `${step.title} — pending, can be caught up`
        : leftBehind
          ? `${step.title} — in progress, section(s) still open`
          : step.title;

  return (
    <li key={step.workflow_step_id} className="relative" style={{ width: 96 }}>
      {idx !== 0 && <div className="absolute left-0 top-5 h-px w-1/2 bg-gray-200" />}
      {idx !== sectionLength - 1 && <div className="absolute right-0 top-5 h-px w-1/2 bg-gray-200" />}
      <button
        type="button"
        disabled={!selectable}
        onClick={() => onSelect(step.candidate_step_id)}
        className={`relative z-10 flex w-full flex-col items-center gap-1.5 rounded-md px-2 py-1.5 transition-colors ${
          isSelected ? 'bg-primary-50' : selectable ? 'hover:bg-gray-50' : ''
        } ${selectable ? 'cursor-pointer' : 'cursor-default'}`}
      >
        <div
          className={`relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
            leftBehind ? 'bg-primary text-white' : meta.className
          } ${overdue ? 'ring-2 ring-red-500' : needsReview ? 'ring-2 ring-blue-400' : gap || leftBehind ? 'ring-2 ring-amber-400' : ''}`}
        >
          <StepIcon code={step.code} />
          {overdue && <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-red-500" />}
          {!overdue && needsReview && <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-blue-400" />}
        </div>
        <span
          className={`max-w-[90px] truncate text-center text-[11px] font-medium ${
            overdue ? 'text-red-600' : isSelected ? 'text-primary' : gap || leftBehind ? 'text-amber-600' : 'text-gray-600'
          }`}
          title={title}
        >
          {step.sort_order}. {step.title}
        </span>
      </button>
    </li>
  );
}

export function StepIconRow({ candidate, selectedStep, onSelect }) {
  // Registration is captured on the candidate creation form now, so it's not shown as a
  // step to click through here — it still exists and gets completed in the background.
  const steps = candidate.steps.filter((step) => step.code !== 'registration');
  const groups = [...(candidate.category?.groups || [])].sort((a, b) => a.sort_order - b.sort_order);

  const sections =
    groups.length > 0
      ? [
          ...groups.map((g) => ({ id: g.id, name: g.name, steps: steps.filter((s) => s.step_group_id === g.id) })),
          { id: 'ungrouped', name: 'Ungrouped', steps: steps.filter((s) => !s.step_group_id) },
        ].filter((section) => section.steps.length > 0)
      : [{ id: 'all', name: null, steps }];

  return (
    <div className="space-y-4">
      {sections.map((section) => (
        <div key={section.id}>
          {section.name && <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">{section.name}</p>}
          <ol className="flex flex-wrap gap-x-1 gap-y-2">
            {section.steps.map((step, idx) => (
              <StepIconItem
                key={step.workflow_step_id}
                step={step}
                idx={idx}
                sectionLength={section.steps.length}
                candidate={candidate}
                selectedStep={selectedStep}
                onSelect={onSelect}
              />
            ))}
          </ol>
        </div>
      ))}
    </div>
  );
}

export function StepDetailPanel({ candidate, selectedStep }) {
  const queryClient = useQueryClient();

  const revertMutation = useMutation({
    mutationFn: () => revertCandidateStep(candidate.id),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Last step reverted.' });
      queryClient.invalidateQueries({ queryKey: ['candidates', String(candidate.id)] });
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not revert.' }),
  });

  if (!selectedStep) return null;

  return (
    <div>
      {candidate.allowed_actions.can_revert && (
        <div className="mb-4 flex justify-end">
          <Button variant="secondary" onClick={() => revertMutation.mutate()} loading={revertMutation.isPending}>
            Revert last step
          </Button>
        </div>
      )}

      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-900">
          {selectedStep.sort_order}. {selectedStep.title}
        </h3>
        {selectedStep.actioned_by && (
          <span className="text-xs text-gray-400">
            {selectedStep.actioned_by} · {new Date(selectedStep.actioned_at).toLocaleDateString()}
          </span>
        )}
      </div>

      {selectedStep.converted_from_category && (
        <p className="mb-3 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-800">
          Carried over from the {selectedStep.converted_from_category} workflow.
          {needsConversionReview(selectedStep) && (
            <>
              {' '}
              This step is missing what the new workflow requires:{' '}
              {missingConversionDetails(selectedStep).join(', ')}.{' '}
              {selectedStep.missing_documents?.length > 0 && 'Upload/verify the document(s) below.'}
              {selectedStep.missing_documents?.length > 0 && selectedStep.missing_fields?.length > 0 && ' '}
              {selectedStep.missing_fields?.length > 0 &&
                (selectedStep.kind === 'task' && selectedStep.status === 'completed'
                  ? 'Use "Edit" below to fill in the field(s).'
                  : 'The missing field(s) can only be filled in on a task step.')}
            </>
          )}
        </p>
      )}

      {selectedStep.status === 'completed' && selectedStep.kind === 'task' ? (
        // Checked ahead of is_current: a workflow's last step stays "current" once completed
        // (nothing left to advance to), but it's done — show its history + edit-request UI,
        // not the live processing form.
        <>
          <StepHistory step={selectedStep} />
          <RequiredDocumentsPanel
            requiredDocuments={selectedStep.required_documents}
            documents={candidate.documents}
            candidateId={candidate.id}
            candidateStepId={selectedStep.candidate_step_id}
            fields={selectedStep.fields}
            answers={selectedStep.answers || {}}
          />
          <CompletedStepEditPanel candidate={candidate} step={selectedStep} />
        </>
      ) : selectedStep.is_current ? (
        <CurrentStepPanel candidate={candidate} step={selectedStep} />
      ) : selectedStep.status === 'pending' && isGapStep(selectedStep, candidate) ? (
        <>
          <p className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
            This step is still pending even though the candidate has moved past it — catch it up below.
          </p>
          <CurrentStepPanel candidate={candidate} step={selectedStep} />
        </>
      ) : selectedStep.status === 'pending' ? (
        <p className="rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-600">This step hasn't been reached yet.</p>
      ) : selectedStep.status === 'in_progress' ? (
        <>
          <p className="mb-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
            The candidate has already moved on, but this step still has section(s) open — finish it below.
          </p>
          <CurrentStepPanel candidate={candidate} step={selectedStep} />
        </>
      ) : selectedStep.status === 'skipped' ? (
        <>
          <p className="mb-3 rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-600">
            This step was skipped — it can still be completed below.
          </p>
          {isHistoryExpandable(selectedStep) && <StepHistory step={selectedStep} />}
          <div className="mt-3">
            <CurrentStepPanel candidate={candidate} step={selectedStep} />
          </div>
        </>
      ) : (
        <>
          {isHistoryExpandable(selectedStep) && <StepHistory step={selectedStep} />}
          <RequiredDocumentsPanel
            requiredDocuments={selectedStep.required_documents}
            documents={candidate.documents}
            candidateId={candidate.id}
            candidateStepId={selectedStep.candidate_step_id}
            fields={selectedStep.fields}
            answers={selectedStep.answers || {}}
          />
        </>
      )}
    </div>
  );
}
